/**
 * List safety — slice 18. **The first file in this product whose job is to stop
 * a send rather than to describe one.**
 *
 * ## The problem, stated once, precisely
 *
 * There are 1,287 real cooperative-bank addresses in this database and not one
 * of them has ever been mailed. Slice 5 materialises the whole segment and
 * `campaign-send.runner.ts` drains it 25 recipients at a time, as fast as the
 * provider will take them. Cold lists of that age routinely bounce 5–15%.
 *
 * Amazon judges the **sending account**, not the campaign, on a rolling rate
 * over recent volume: at or above {@link AWS_BOUNCE_RATE_REVIEW} the account is
 * placed under review, at or above `AWS_BOUNCE_RATE_PAUSE` sending is paused.
 * Under bring-your-own-SES that account belongs to the **customer**. So a single
 * cold first campaign can get a customer reviewed, and Wend would have caused
 * it.
 *
 * Slices 6, 10 and 11 built every instrument needed to *observe* that — delivery
 * events, health bands, per-ISP bounces — and nothing whatsoever to *prevent*
 * it. This file is the prevention, and it is four distinct defences. None of
 * them substitutes for another and the file is arranged in that order:
 *
 *   1. {@link listHealth} — a pre-flight score, **completely offline**;
 *   2. {@link warmUpPlanFor} — a seed and a ramp, which is the actual prevention;
 *   3. {@link mustCampaignHalt} — an automatic stop, stricter than AWS;
 *   4. the sentence constants at the foot, because a defence nobody can read is
 *      a defence nobody acts on.
 *
 * ## THE VERDICT LIVES HERE. THE GATHERING LIVES IN THE API.
 *
 * This is `campaign-readiness.ts`'s split, followed exactly. Every decision
 * below is a **pure function of values both sides already hold**, with no I/O,
 * for the reason `campaignScheduleIssues` states in its own header: two
 * implementations of "may this send" drift, and the way they drift is that one
 * of them stops checking. The API reads the counts; this file decides what they
 * mean; the wizard renders the same verdict the API refused with.
 *
 * ## THE HEALTH CHECK MAKES NO NETWORK CALL, AND THAT IS A REQUIREMENT
 *
 * No MX lookup, no SMTP probe, no third-party validation API. Two reasons, and
 * the first one is not negotiable: handing 1,287 real bank addresses to a
 * verification vendor is a data-exfiltration event with a purchase order
 * attached. The second is that it would make a *readiness screen* depend on a
 * third party being up. Every signal {@link ListHealthSignals} carries is
 * derivable from tables this database already has.
 *
 * The price is stated on screen rather than buried here:
 * {@link listHealthUnknowableNote} — four words, per the Shipping Reach text
 * rule. An offline check cannot tell a live mailbox from a dead one. That is
 * exactly why defences 2 and 3 exist and why a `ready` verdict still gets a
 * warm-up plan: **the check is advisory, and it is never the only thing between
 * a user and a bad send.**
 *
 * ## THE DENOMINATOR, WHICH IS THE DECISION THE WHOLE SLICE TURNS ON
 *
 * A bounce rate can be taken over *accepted* (messages the provider took) or
 * over *settled* (messages a delivery event has since answered for). They are
 * not close, and choosing wrongly is how this slice fails silently.
 *
 * `accepted` is wrong. Bounces arrive **asynchronously**, minutes to hours
 * later, by SNS. At the instant a wave finishes, `accepted` is 250 and bounces
 * are 0 — not because the list is clean but because AWS has not answered yet.
 * Dividing by `accepted` therefore reports 0% for every wave that has just
 * ended, which releases the next wave on a denominator that has not settled.
 * That is the precise failure this slice exists to prevent, arrived at through
 * the arithmetic instead of through the code.
 *
 * So the denominator is `settled` — {@link SendRiskMeasurement.settled}, the
 * count of messages this campaign has a *terminal delivery outcome* for. It
 * errs in the safe direction: early on it over-states the rate, because the
 * first event back can be a bounce. Two guards make that safe rather than
 * jumpy — a minimum sample ({@link EMAIL_SEND_RISK_GATE}`.minimumSettledOutcomes`)
 * and a minimum coverage ({@link WAVE_SETTLED_SHARE_REQUIRED}) — and the third
 * state, *no events at all*, is neither "fine" nor "bad" and gets its own hold
 * reason rather than a zero.
 *
 * **`accepted` is not a field on {@link SendRiskMeasurement} at all.** It was
 * removed rather than documented as unusable: a number that must never be a
 * denominator cannot be one if it is not in the object. The screen gets it from
 * `campaignProgressReading`, which is where progress has always lived.
 *
 * Every count feeding these fields is `countDistinct(message_id)`. SES, SNS and
 * Meta all re-notify, and this codebase has shipped `count(*)` over
 * `delivery_events` three separate times.
 *
 * ## WHATSAPP IS IN SCOPE, ON A DIFFERENT SIGNAL, AND THE EMAIL THRESHOLD IS
 * ## STRUCTURALLY UNREACHABLE FROM IT
 *
 * WhatsApp has no bounce rate in the SES sense — Meta has an opaque per-number
 * quality rating and a messaging limit, and publishes no percentage at all. So
 * the WhatsApp gate measures what slice 12 already ingests: the share of settled
 * messages whose failure class means the number **cannot receive messages**
 * (`WA_SUPPRESSING_FAILURE_CLASSES`), which is the input that actually drags a
 * number's quality rating down.
 *
 * This repository has already shipped the defect where an AWS email threshold
 * was applied across every channel — a workspace sitting exactly on AWS's 5%
 * line rendered as 1.55% behind a green badge. So it is made **impossible**
 * here, not merely avoided:
 *
 *   * {@link SendRiskMeasurement} is a discriminated union on `channel` whose two
 *     arms have **different numerator field names** (`hardBounced` versus
 *     `undeliverable`), so they are mutually non-assignable — slice 14's
 *     `Reach`/`RowCount` precedent;
 *   * **no exported function takes a threshold argument.** There is nowhere to
 *     pass the wrong one. {@link sendRiskGateFor} is the only way to obtain a
 *     gate, and it is keyed on the channel the measurement itself carries;
 *   * the halt reason is selected from the same gate, so a WhatsApp halt cannot
 *     be described with email's sentence.
 *
 * `isBounceMeasured` in `./delivery.js` already says bounce rate is email-only.
 * This file does not contradict it: the WhatsApp figure is deliberately not
 * called a bounce rate anywhere, in any type, constant or sentence.
 *
 * ## WHERE THE ARROWS POINT
 *
 * A **leaf**. It imports `../enums.js` (the channel and campaign-status
 * vocabularies), `./delivery.js` (AWS's own numbers, so the halt threshold is
 * derived from the line it exists to stay under rather than typed out beside
 * it), and `./overview.js` (`rateReading`, so there is one implementation of
 * "is there enough volume for this percentage to mean anything" and this file is
 * not the second). Nothing in `@wend/shared` imports it back, so it cannot close
 * a cycle between two modules that both build zod schemas at load — the failure
 * that shows up as `z.enum(undefined)` at import rather than as a review
 * comment.
 *
 * It pointedly does **not** import `./campaign.js`. It needs `CampaignStatus`,
 * which lives in `../enums.js`, and nothing else; reaching into the campaign
 * contract would put the file that decides whether a campaign may send in a
 * cycle with the file that decides whether it may be scheduled.
 *
 * ## EVERY GATE FAILS CLOSED, AND THAT IS WRITTEN AS THE DEFAULT BRANCH
 *
 * An unreadable plan, a missing wave row, an unparseable verdict, a rate that
 * cannot be computed: every one of them **holds**, and none of them releases.
 * {@link mayWaveRelease} takes a nullable measurement and a nullable previous
 * wave and answers `release: false` for both. {@link mustCampaignHalt} halts on
 * a null measurement rather than sending on blind — see
 * {@link CAMPAIGN_HALT_REASON_VALUES}`.risk_unmeasurable`, where the cost of
 * that choice is stated rather than hidden.
 */
import { z } from 'zod';

import {
  channelSchema,
  type CampaignStatus,
  type Channel,
} from '../enums.js';
import { AWS_BOUNCE_RATE_REVIEW } from './delivery.js';
import { rateReading, rateReadingSchema, type RateReading } from './overview.js';

/* -------------------------------------------------------------------------- */
/* 1. LIST HEALTH — the offline pre-flight                                     */
/* -------------------------------------------------------------------------- */

/**
 * The one sentence this screen owes the reader about what it cannot do — **in
 * its own channel's words**.
 *
 * Four words, on screen, always visible — the Shipping Reach text rule's middle
 * row: *honesty about a number stays visible, but four words rather than a
 * paragraph*. An offline check reads addresses; it does not knock on mailboxes.
 * Every other explanation of that belongs in this header and is deleted from the
 * UI.
 *
 * Keyed on `Channel` and exhaustive, like every other user-visible table in this
 * file: "cannot detect dead mailboxes" on a WhatsApp campaign is the wart slice
 * 17 recorded on `CAMPAIGN_STATUS_DISPLAY.sending`, and this file is where that
 * pattern is refused rather than copied. A third channel is a compile error.
 */
export const LIST_HEALTH_UNKNOWABLE_NOTE_BY_CHANNEL: Readonly<Record<Channel, string>> = {
  email: 'Cannot detect dead mailboxes.',
  whatsapp: 'Cannot detect numbers not on WhatsApp.',
};

/** The only supported way to obtain the note. There is no channel-free version. */
export function listHealthUnknowableNote(channel: Channel): string {
  return LIST_HEALTH_UNKNOWABLE_NOTE_BY_CHANNEL[channel];
}

/**
 * Local parts that belong to a **desk, not a person**.
 *
 * Role addresses bounce and complain far above list average: nobody chose to
 * receive marketing at `info@`, the box is often an unattended alias, and it is
 * frequently the address a spam-trap operator recycles. A cooperative-bank list
 * imported from a summit registration is dense with them, which is exactly why
 * this signal is measured on a list where it will not read zero.
 *
 * The list is deliberately short and unambiguous. A longer one — every plausible
 * departmental name — starts flagging `accounts@` and `branch@` at a bank, where
 * those are the addresses a person actually reads. Over-flagging teaches the
 * reader to ignore the number, which costs more than the misses.
 */
export const ROLE_ADDRESS_LOCAL_PARTS: readonly string[] = [
  'abuse',
  'admin',
  'administrator',
  'billing',
  'contact',
  'enquiries',
  'enquiry',
  'help',
  'hostmaster',
  'info',
  'mail',
  'marketing',
  'noreply',
  'no-reply',
  'office',
  'postmaster',
  'sales',
  'support',
  'webmaster',
];

const ROLE_ADDRESS_SET = new Set(ROLE_ADDRESS_LOCAL_PARTS);

/**
 * The same shape `campaigns_from_email_shape` enforces in `0010_campaigns.sql`:
 * something, an `@`, something, a dot, something, and no whitespace anywhere.
 *
 * Deliberately weak, and deliberately the *same* weakness. A stricter pattern
 * here would report addresses as invalid that the database happily stores and
 * SES happily accepts, which is a health check inventing a problem. The reverse
 * error — an address this accepts and SES rejects — is caught by the send path,
 * where it costs one `failed` row.
 */
const MAILABLE_EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Whether an address is shaped like something SES could be handed. */
export function isSyntacticallyMailableEmail(value: string): boolean {
  return MAILABLE_EMAIL_SHAPE.test(value.trim());
}

/** The domain half, lower-cased, or `null` when there is not one. */
export function emailDomain(value: string): string | null {
  const at = value.trim().toLowerCase().lastIndexOf('@');
  if (at <= 0) return null;
  const domain = value.trim().toLowerCase().slice(at + 1);
  return domain === '' ? null : domain;
}

/** Whether the local part is a desk rather than a person. */
export function isRoleAddress(value: string): boolean {
  const at = value.trim().toLowerCase().indexOf('@');
  if (at <= 0) return false;
  // The `+tag` half of an address is the sender's, not the mailbox's.
  const local = value.trim().toLowerCase().slice(0, at).split('+')[0] ?? '';
  return ROLE_ADDRESS_SET.has(local);
}

/**
 * What the API counted, and the only input {@link listHealth} is allowed.
 *
 * Every field is a **count of distinct addresses in this segment on this
 * channel**, not a count of contact rows — the 1,287-versus-1,278 distinction
 * slice 2 settled. `duplicates` is the difference between the two, and it is the
 * one field that counts rows deliberately.
 *
 * `neverMailed` is the coldness signal and the most important one on this list.
 * It is derived from whether the tenant has ever handed a message to a provider
 * for that address, which is a `messages` join — not from a contact's age, which
 * says nothing about whether we have mailed them.
 *
 * `previouslyHardBounced` is `countDistinct(message_id)` behind a permanent
 * bounce, mapped back to addresses. Provider rows repeat; people do not.
 */
export const listHealthSignalsSchema = z
  .object({
    channel: channelSchema,
    /** Distinct addresses this campaign would reach on this channel. */
    total: z.number().int().min(0),
    /** Addresses that are not shaped like an address. */
    syntacticallyInvalid: z.number().int().min(0),
    /** Addresses belonging to a desk rather than a person. Email only; 0 on WhatsApp. */
    roleAddresses: z.number().int().min(0),
    /** Addresses already on `suppressions` for this tenant and channel. */
    suppressed: z.number().int().min(0),
    /** Addresses this tenant has had a permanent bounce for before. */
    previouslyHardBounced: z.number().int().min(0),
    /** Addresses this tenant has never handed a message to a provider for. */
    neverMailed: z.number().int().min(0),
    /** Contact rows that repeat an address another row already carries. */
    duplicates: z.number().int().min(0),
    /** How many addresses sit on the single largest domain (or number prefix). */
    largestGroupCount: z.number().int().min(0),
    /** The domain (or number prefix) that group belongs to. Null when there is none. */
    largestGroupKey: z.string().nullable(),
    /** How many distinct domains (or number prefixes) the list spans. */
    distinctGroups: z.number().int().min(0),
  })
  .strict();
export type ListHealthSignals = z.infer<typeof listHealthSignalsSchema>;

/* ---- The thresholds, each with the reasoning that produced it ------------- */

/**
 * Share of the list on **one** domain at which concentration is worth saying,
 * and the share at which it dominates the outcome.
 *
 * A list that is 60% one domain fails or succeeds all at once: one receiving
 * system's filter decides the campaign, and a seed drawn badly proves nothing
 * about the rest. That is why concentration is a *health* signal and not merely
 * a curiosity — it is the fact that makes {@link seedByGroupSpread} necessary
 * rather than tidy.
 *
 * A third at `concern` because that is roughly where one domain stops being a
 * coincidence on a list of a few hundred; a half at `severe` because past it,
 * the majority verdict *is* that domain's verdict.
 */
export const DOMAIN_CONCENTRATION_CONCERN = 0.3;
export const DOMAIN_CONCENTRATION_SEVERE = 0.5;

/**
 * Share of role addresses. Above a sixth is unusual for a list somebody has
 * permission to mail; above a third the list is a directory rather than an
 * audience, and it will generate complaints from people who never subscribed.
 */
export const ROLE_ADDRESS_SHARE_CONCERN = 0.15;
export const ROLE_ADDRESS_SHARE_SEVERE = 0.33;

/**
 * Share of the list that has never been mailed. **The coldness signal.**
 *
 * `concern` at a half and `severe` at nearly all of it, and the second one is
 * the state this whole slice was commissioned for: a list that is 100% never
 * mailed has no evidence behind it at all, and the first campaign is the
 * experiment. It is not a fault — every list starts here — which is why it
 * produces a warm-up plan rather than a refusal.
 */
export const NEVER_MAILED_SHARE_CONCERN = 0.5;
export const NEVER_MAILED_SHARE_SEVERE = 0.9;

/**
 * Share of the list that has hard-bounced for this tenant before.
 *
 * Much tighter than the other two, because this is not a proxy for risk — it is
 * risk that has already happened once, to this account, at these addresses.
 * `concern` sits an order of magnitude below AWS's review line and `severe` sits
 * on it: a list where one address in twenty is a known dead mailbox will
 * reproduce that number the moment it is mailed.
 */
export const PRIOR_HARD_BOUNCE_SHARE_CONCERN = 0.005;
export const PRIOR_HARD_BOUNCE_SHARE_SEVERE = AWS_BOUNCE_RATE_REVIEW;

/**
 * Share of syntactically invalid addresses. Any at all is worth a note — a
 * malformed address is a certain failure, not a probable one — and a twentieth
 * means the import mapping is wrong rather than the list being dirty.
 */
export const INVALID_SYNTAX_SHARE_CONCERN = 0.001;
export const INVALID_SYNTAX_SHARE_SEVERE = 0.05;

/* ---- The vocabulary ------------------------------------------------------ */

/**
 * How badly one signal reads. Three levels and no fourth.
 *
 * Deliberately **not** `HealthBand`'s `ok | warning | critical`, even though the
 * shape rhymes. That vocabulary means "a measured rate against a published
 * provider threshold"; this one means "a property of a list nobody has mailed
 * yet". Sharing the words would let a component render one with the authority of
 * the other, which is the category error slice 11's contracts were shaped to
 * prevent.
 */
export const LIST_HEALTH_SEVERITY_VALUES = ['note', 'concern', 'severe'] as const;
export const listHealthSeveritySchema = z.enum(LIST_HEALTH_SEVERITY_VALUES);
export type ListHealthSeverity = z.infer<typeof listHealthSeveritySchema>;

/** What can be said about a list before anything has been sent to it. */
export const LIST_HEALTH_REASON_VALUES = [
  'never_mailed',
  'domain_concentration',
  'role_addresses',
  'prior_hard_bounces',
  'suppressed_present',
  'syntactically_invalid',
  'duplicates',
  'nothing_to_send',
] as const;
export const listHealthReasonSchema = z.enum(LIST_HEALTH_REASON_VALUES);
export type ListHealthReason = z.infer<typeof listHealthReasonSchema>;

/**
 * The verdict. Four bands, and the vocabulary is chosen so that none of them
 * reads as a refusal — because none of them is one.
 *
 *   `ready`           nothing in the data argues against sending
 *   `warm_up_advised` ordinary for a list that has not been mailed
 *   `high_risk`       several signals elevated, or one badly
 *   `do_not_send`     the strongest thing an offline check may say
 *
 * **`do_not_send` is advice, and the schedule path still allows it.** That is
 * deliberate and it is the hardest call in the file. A pre-flight that refuses
 * would be a pre-flight people learn to route around — the quiet-hours lesson,
 * which SLICES states as *a rule that refuses the common case to prevent the
 * rare one gets worked around rather than heeded*. What `do_not_send` does
 * instead is drive the most conservative warm-up plan there is, and the halt in
 * defence 3 is what actually stops the send. Advice that changes the plan is
 * worth more than a refusal that changes the workaround.
 */
export const LIST_HEALTH_VERDICT_VALUES = [
  'ready',
  'warm_up_advised',
  'high_risk',
  'do_not_send',
] as const;
export const listHealthVerdictSchema = z.enum(LIST_HEALTH_VERDICT_VALUES);
export type ListHealthVerdict = z.infer<typeof listHealthVerdictSchema>;

/** One thing wrong with a list, as a whole sentence somebody can act on. */
export const listHealthFindingSchema = z
  .object({
    code: listHealthReasonSchema,
    severity: listHealthSeveritySchema,
    /** A whole sentence, safe to show a customer. Never a fragment, never a label. */
    message: z.string(),
  })
  .strict();
export type ListHealthFinding = z.infer<typeof listHealthFindingSchema>;

/**
 * The score, its reasons, and the note about what it cannot know.
 *
 * `checkedAt` is on the report rather than assumed by the reader, for the reason
 * slice 13 puts a window on a report: a verdict is a snapshot of a segment that
 * can be re-materialised underneath it, and a snapshot that cannot say when it
 * was taken cannot be trusted a day later.
 */
export const listHealthReportSchema = z
  .object({
    verdict: listHealthVerdictSchema,
    findings: z.array(listHealthFindingSchema),
    signals: listHealthSignalsSchema,
    /** Always {@link listHealthUnknowableNote} for this report's channel. On the wire so the screen cannot forget it. */
    unknowable: z.string(),
    checkedAt: z.string(),
  })
  .strict();
export type ListHealthReport = z.infer<typeof listHealthReportSchema>;

/** A share, with a zero denominator answering `0` rather than `NaN`. */
function share(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(1, Math.max(0, part) / whole);
}

/** `n` formatted the way every count in this product is. */
function count(value: number): string {
  return value.toLocaleString('en-IN');
}

/* ---- The sentences, per channel, written whole ---------------------------- */

/**
 * Every sentence {@link listHealth} can put on a screen, **keyed on the
 * channel the signals were gathered on**.
 *
 * ## WHY THIS IS A `Record<Channel, …>` AND NOT A NOUN SUBSTITUTION
 *
 * The superseded version wrote one set of sentences and rendered them on either
 * channel. Three of them were email's:
 *
 *   * *"Amazon counts every one of those against your account"* on a campaign
 *     going through Meta;
 *   * *"One receiving system will decide the outcome of the whole campaign"*
 *     interpolated with `largestGroupKey`, which on WhatsApp is
 *     `left(address, 5)` — a dialling prefix, so it read *"98% of this list sits
 *     on +9198"* and called a country code a receiving system;
 *   * *"addresses"*, of phone numbers, throughout.
 *
 * This is reachable and near-certain on the real data: 752 real Indian numbers,
 * none ever messaged, so `neverMailed / total` is 1.0 and the concentration is
 * near-total. Both findings fire `severe`, so the verdict is `high_risk` or
 * `do_not_send` — the two whose detail copy names Amazon.
 *
 * A noun table (`address`/`number`) was rejected: two of the three defects were
 * not nouns but **claims**, and a sentence assembled from parts is a sentence
 * nobody reads whole. So each channel's sentences are written out, the record is
 * exhaustive over `Channel`, and a third channel does not compile until somebody
 * writes its words.
 *
 * ## WHAT CONCENTRATION MEANS ON WHATSAPP, SINCE IT IS NOT WHAT IT MEANS ON EMAIL
 *
 * On email a dominant domain is a *deliverability* fact: one receiving system's
 * filter decides the campaign. On WhatsApp there is no such system — Meta
 * delivers to a handset — so the prefix says nothing about whether messages
 * arrive. The severity is nonetheless unchanged, and the WhatsApp sentence says
 * what it is actually a fact about: {@link seedByGroupSpread} picks the first
 * batch by spreading it *across* groups, and with one group there is nothing to
 * spread, so a clean seed proves less about the rest of the list than usual. A
 * weaker measurement is a reason to ramp more slowly, which is exactly what the
 * verdict does with it — so the threshold was left alone rather than lowered to
 * suit a sentence.
 *
 * ## `roleAddresses` IS ABSENT ON WHATSAPP RATHER THAN ZERO-WORDED
 *
 * A phone number has no local part, so the signal is `(select 0::int)` in
 * `campaign-list-health.ts` and the finding cannot fire. Writing WhatsApp copy
 * for it would be copy that is unreachable today and wrong the day somebody
 * makes the count non-zero. The builder is optional instead, and its absence is
 * what stops the finding being raised — structural rather than accidental.
 *
 * `syntacticallyInvalid` is **not** treated the same way, deliberately: it is
 * zero on WhatsApp only because `contacts_phone_e164_format` refuses a malformed
 * number at write time, and if that CHECK is ever dropped the count becomes real
 * and needs a sentence. It has one.
 */
interface ListHealthSentences {
  readonly nothingToSend: string;
  readonly neverMailedSevere: (neverMailed: number, total: number) => string;
  readonly neverMailedConcern: (neverMailed: number, total: number) => string;
  readonly concentrationSevere: (percent: number, group: string) => string;
  readonly concentrationConcern: (percent: number, group: string) => string;
  /** Absent on a channel where the signal cannot be non-zero. See the header. */
  readonly roleAddresses?: {
    readonly severe: (roleAddresses: number) => string;
    readonly concern: (roleAddresses: number) => string;
  };
  readonly priorFailuresSevere: (previouslyFailed: number) => string;
  readonly priorFailuresConcern: (previouslyFailed: number) => string;
  readonly suppressed: (suppressed: number) => string;
  readonly invalidSevere: (invalid: number) => string;
  readonly invalidConcern: (invalid: number) => string;
  readonly duplicates: (duplicates: number) => string;
}

const LIST_HEALTH_SENTENCES_BY_CHANNEL: Readonly<Record<Channel, ListHealthSentences>> = {
  email: {
    nothingToSend:
      'Nobody in this segment can be reached by email, so there is nothing to check and nothing to send.',
    neverMailedSevere: (neverMailed, total) =>
      `You have never sent to ${count(neverMailed)} of these ${count(total)} addresses, so nothing here has been proved to work. This is the ordinary state of a first campaign, and it is why Wend sends a small batch first and waits.`,
    neverMailedConcern: (neverMailed, total) =>
      `${count(neverMailed)} of these ${count(total)} addresses have never been sent to, so more than half of this list is untested.`,
    concentrationSevere: (percent, group) =>
      `${percent}% of this list sits on ${group}. One receiving system will decide the outcome of the whole campaign, so Wend spreads the first batch deliberately rather than taking the first few addresses.`,
    concentrationConcern: (percent, group) =>
      `${percent}% of this list sits on ${group}, so that one destination carries most of the result.`,
    roleAddresses: {
      severe: (roleAddresses) =>
        `${count(roleAddresses)} addresses go to a desk rather than a person — info@, support@ and the like. These bounce and get marked as spam far more often than personal addresses.`,
      concern: (roleAddresses) =>
        `${count(roleAddresses)} addresses go to a desk rather than a person, which raises both bounces and spam complaints.`,
    },
    priorFailuresSevere: (previouslyFailed) =>
      `${count(previouslyFailed)} of these addresses have already bounced permanently for you. Sending to them again bounces again, and Amazon counts every one of those against your account.`,
    priorFailuresConcern: (previouslyFailed) =>
      `${count(previouslyFailed)} of these addresses have bounced permanently before and will bounce again.`,
    suppressed: (suppressed) =>
      `${count(suppressed)} people on this list have unsubscribed or been suppressed. They will be skipped with a reason, not sent to.`,
    invalidSevere: (invalid) =>
      `${count(invalid)} entries are not addresses at all. That many usually means a column was mapped wrongly when the list was imported.`,
    invalidConcern: (invalid) =>
      `${count(invalid)} entries are not addresses and cannot be sent to.`,
    duplicates: (duplicates) =>
      `${count(duplicates)} rows repeat an address another row already has. Each person is sent to once.`,
  },
  whatsapp: {
    nothingToSend:
      'Nobody in this segment can be reached on WhatsApp, so there is nothing to check and nothing to send.',
    neverMailedSevere: (neverMailed, total) =>
      `You have never messaged ${count(neverMailed)} of these ${count(total)} numbers, so nothing here has been proved to work. This is the ordinary state of a first campaign, and it is why Wend sends a small batch first and waits.`,
    neverMailedConcern: (neverMailed, total) =>
      `${count(neverMailed)} of these ${count(total)} numbers have never been messaged, so more than half of this list is untested.`,
    // Never "one receiving system": Meta delivers to a handset, and the group is
    // a dialling prefix. What it is genuinely a fact about is the seed — see the
    // header above.
    concentrationSevere: (percent, group) =>
      `${percent}% of these numbers begin ${group}, so almost the whole list is on one country and network range. Wend picks the first batch by spreading it across ranges, and with only one there is nothing to spread — so a clean first batch proves less about the rest of this list than usual.`,
    concentrationConcern: (percent, group) =>
      `${percent}% of these numbers begin ${group}, so the first batch has little to spread across.`,
    priorFailuresSevere: (previouslyFailed) =>
      `${count(previouslyFailed)} of these numbers have already failed permanently for you — WhatsApp could not reach them. Sending to them again fails again, and Meta counts that against your number’s quality rating.`,
    priorFailuresConcern: (previouslyFailed) =>
      `${count(previouslyFailed)} of these numbers have failed permanently before and will fail again.`,
    suppressed: (suppressed) =>
      `${count(suppressed)} people on this list have opted out or been blocked. They will be skipped with a reason, not sent to.`,
    invalidSevere: (invalid) =>
      `${count(invalid)} entries are not usable phone numbers. That many usually means a column was mapped wrongly when the list was imported.`,
    invalidConcern: (invalid) =>
      `${count(invalid)} entries are not usable phone numbers and cannot be sent to.`,
    duplicates: (duplicates) =>
      `${count(duplicates)} rows repeat a number another row already has. Each person is sent to once.`,
  },
};

/**
 * The verdict, from the counts. **Pure, offline, and the only implementation.**
 *
 * The verdict is the worst severity present, mapped one-for-one:
 * no findings → `ready`, a `note` or a `concern` → `warm_up_advised`,
 * one `severe` → `high_risk`, two or more → `do_not_send`. It is a count of
 * severities rather than a weighted score because a score is a number nobody can
 * argue with in review, and every threshold above is a number somebody has to be
 * able to argue with.
 *
 * The empty list is its own verdict rather than a `ready` with no findings:
 * `campaignScheduleIssues` already blocks a campaign that reaches nobody, and a
 * green tick on a list of zero is the zero-versus-absent defect wearing a
 * different hat.
 */
export function listHealth(signals: ListHealthSignals, checkedAt: string): ListHealthReport {
  const findings: ListHealthFinding[] = [];
  const total = signals.total;
  /*
   * Every sentence below comes from here, chosen by the channel the signals were
   * gathered on. Nothing in this function composes a sentence from parts — see
   * `LIST_HEALTH_SENTENCES_BY_CHANNEL`'s header for the three email-only claims
   * that were being rendered on WhatsApp campaigns.
   */
  const words = LIST_HEALTH_SENTENCES_BY_CHANNEL[signals.channel];
  const unknowable = listHealthUnknowableNote(signals.channel);

  const push = (code: ListHealthReason, severity: ListHealthSeverity, message: string): void => {
    findings.push({ code, severity, message });
  };

  if (total === 0) {
    push('nothing_to_send', 'severe', words.nothingToSend);
    return { verdict: 'do_not_send', findings, signals, unknowable, checkedAt };
  }

  const neverMailedShare = share(signals.neverMailed, total);
  if (neverMailedShare >= NEVER_MAILED_SHARE_SEVERE) {
    push('never_mailed', 'severe', words.neverMailedSevere(signals.neverMailed, total));
  } else if (neverMailedShare >= NEVER_MAILED_SHARE_CONCERN) {
    push('never_mailed', 'concern', words.neverMailedConcern(signals.neverMailed, total));
  }

  const concentration = share(signals.largestGroupCount, total);
  const percent = Math.round(concentration * 100);
  const groupName = signals.largestGroupKey ?? 'one destination';
  if (concentration >= DOMAIN_CONCENTRATION_SEVERE) {
    push('domain_concentration', 'severe', words.concentrationSevere(percent, groupName));
  } else if (concentration >= DOMAIN_CONCENTRATION_CONCERN) {
    push('domain_concentration', 'concern', words.concentrationConcern(percent, groupName));
  }

  /*
   * Absent on a channel where a role address cannot exist, which is what stops
   * the finding being raised there rather than the count happening to be zero.
   * See `ListHealthSentences`.
   */
  const roleWords = words.roleAddresses;
  if (roleWords !== undefined) {
    const roleShare = share(signals.roleAddresses, total);
    if (roleShare >= ROLE_ADDRESS_SHARE_SEVERE) {
      push('role_addresses', 'severe', roleWords.severe(signals.roleAddresses));
    } else if (roleShare >= ROLE_ADDRESS_SHARE_CONCERN) {
      push('role_addresses', 'concern', roleWords.concern(signals.roleAddresses));
    }
  }

  const bounceShare = share(signals.previouslyHardBounced, total);
  if (signals.previouslyHardBounced > 0 && bounceShare >= PRIOR_HARD_BOUNCE_SHARE_SEVERE) {
    push('prior_hard_bounces', 'severe', words.priorFailuresSevere(signals.previouslyHardBounced));
  } else if (signals.previouslyHardBounced > 0 && bounceShare >= PRIOR_HARD_BOUNCE_SHARE_CONCERN) {
    push('prior_hard_bounces', 'concern', words.priorFailuresConcern(signals.previouslyHardBounced));
  }

  if (signals.suppressed > 0) {
    push('suppressed_present', 'note', words.suppressed(signals.suppressed));
  }

  const invalidShare = share(signals.syntacticallyInvalid, total);
  if (signals.syntacticallyInvalid > 0 && invalidShare >= INVALID_SYNTAX_SHARE_SEVERE) {
    push('syntactically_invalid', 'severe', words.invalidSevere(signals.syntacticallyInvalid));
  } else if (signals.syntacticallyInvalid > 0 && invalidShare >= INVALID_SYNTAX_SHARE_CONCERN) {
    push('syntactically_invalid', 'concern', words.invalidConcern(signals.syntacticallyInvalid));
  }

  if (signals.duplicates > 0) {
    push('duplicates', 'note', words.duplicates(signals.duplicates));
  }

  const severeCount = findings.filter((finding) => finding.severity === 'severe').length;
  const verdict: ListHealthVerdict =
    severeCount >= 2
      ? 'do_not_send'
      : severeCount === 1
        ? 'high_risk'
        : findings.length > 0
          ? 'warm_up_advised'
          : 'ready';

  return { verdict, findings, signals, unknowable, checkedAt };
}

/* -------------------------------------------------------------------------- */
/* 2. THE RISK GATE — one per channel, and the only source of a threshold      */
/* -------------------------------------------------------------------------- */

/**
 * How much stricter than AWS a **campaign** must be, as a fraction of AWS's
 * review line.
 *
 * The arithmetic that fixes it, written out because a number like this is
 * otherwise indistinguishable from a preference:
 *
 * AWS measures the **whole account** over a rolling window of recent volume.
 * Wend measures **one campaign**. Those are not the same denominator, and the
 * relationship between them is what the number has to survive. Suppose an
 * account's recent volume is `V` and this campaign contributes `C` of it at a
 * bounce rate `r`, with everything else bouncing at zero. The account's rolling
 * rate is `r × C / V`. For a first real campaign to a cold list, `C / V` is
 * close to 1 — the campaign *is* the account's recent volume, because nothing
 * else has been sent. So the account rate and the campaign rate converge, and a
 * campaign allowed to run to AWS's own 5% has put the account at 5%: **the
 * review has already happened by the time the campaign notices.**
 *
 * A threshold therefore has to leave room for the observation lag as well. Two
 * fifths of the review line means a campaign is stopped while the account figure
 * it is dragging upward is still under half of AWS's line — which is where
 * `OVERVIEW_WARNING_FRACTION_OF_THRESHOLD` already puts *amber*, and this is a
 * stop rather than a colour, so it is stricter than that by the same argument
 * that made amber stricter than red.
 *
 * A fraction rather than a fresh constant, so that if AWS moves its line this
 * moves with it rather than silently becoming a second, looser threshold.
 */
export const CAMPAIGN_HALT_FRACTION_OF_AWS_REVIEW = 0.4;

/**
 * 2% of settled messages bouncing permanently stops an email campaign.
 *
 * Rounded to four decimal places on purpose: `0.05 * 0.4` in IEEE-754 is
 * `0.020000000000000004`, and a threshold that renders as `2.0000000000000004%`
 * anywhere is a threshold somebody replaces with a literal.
 */
export const EMAIL_HARD_BOUNCE_HALT_RATE =
  Math.round(AWS_BOUNCE_RATE_REVIEW * CAMPAIGN_HALT_FRACTION_OF_AWS_REVIEW * 10_000) / 10_000;

/**
 * 5% of settled WhatsApp messages failing as unreachable stops a WhatsApp
 * campaign.
 *
 * **This number is Wend's, and it is not Meta's, and the difference is stated
 * here because everything above it cites AWS.** Meta publishes no failure-rate
 * threshold at all: it publishes a per-number *quality rating* — green, yellow,
 * red — computed from signals it does not disclose, and a messaging limit tier
 * that steps up with sustained good behaviour. There is no published line to
 * derive a fraction of.
 *
 * So this is the rate at which a list is visibly wrong rather than a rate Meta
 * has announced: one message in twenty going to a number that cannot receive
 * WhatsApp is not a marginal list, it is the wrong list, and it is exactly the
 * input that moves a number's quality rating and eventually its tier. It is
 * looser than the email number because the consequence is slower and reversible
 * — a rating recovers with good sending, where an AWS review does not un-happen.
 *
 * It is deliberately **not** derived from {@link EMAIL_HARD_BOUNCE_HALT_RATE}.
 * Deriving it would encode the claim that the two channels' risks are
 * proportional, which is the claim that produced this codebase's worst shipped
 * defect.
 */
export const WHATSAPP_UNDELIVERABLE_HALT_RATE = 0.05;

/**
 * The smallest number of settled outcomes at which a rate is allowed to stop
 * anything.
 *
 * **A 1-of-1 bounce is 100% and must not halt a campaign.** Generalised: no
 * single failure may halt on its own, at any threshold. The smallest sample in
 * which one failure is *below* the threshold is `floor(1/rate) + 1` — 51 at 2%,
 * 21 at 5% — so the smallest thing that can ever halt a campaign is two
 * failures, which is a pattern rather than an accident.
 *
 * Derived from the threshold rather than chosen, so the two cannot drift into a
 * state where one failure halts.
 */
function smallestSampleWhereOneFailureIsBelow(rate: number): number {
  if (!(rate > 0)) return Number.POSITIVE_INFINITY;
  return Math.floor(1 / rate) + 1;
}

/**
 * A channel's stopping rule: what is counted, what the line is, and how much
 * has to be counted before the line means anything.
 *
 * The `measure` field is what makes the two non-interchangeable in prose as well
 * as in types — a WhatsApp gate can never be described as a bounce rate,
 * because it does not carry the word.
 */
export interface SendRiskGate {
  readonly channel: Channel;
  /** What the numerator counts. Two different words for two different things. */
  readonly measure: 'permanent_bounce' | 'undeliverable_number';
  /** The share of settled outcomes at or above which the campaign halts. */
  readonly haltRate: number;
  /** Settled outcomes required before {@link haltRate} may stop anything. */
  readonly minimumSettledOutcomes: number;
}

export const EMAIL_SEND_RISK_GATE: SendRiskGate = {
  channel: 'email',
  measure: 'permanent_bounce',
  haltRate: EMAIL_HARD_BOUNCE_HALT_RATE,
  minimumSettledOutcomes: smallestSampleWhereOneFailureIsBelow(EMAIL_HARD_BOUNCE_HALT_RATE),
};

export const WHATSAPP_SEND_RISK_GATE: SendRiskGate = {
  channel: 'whatsapp',
  measure: 'undeliverable_number',
  haltRate: WHATSAPP_UNDELIVERABLE_HALT_RATE,
  minimumSettledOutcomes: smallestSampleWhereOneFailureIsBelow(WHATSAPP_UNDELIVERABLE_HALT_RATE),
};

/**
 * Every gate, keyed by channel. **The only place a threshold comes from.**
 *
 * Exhaustive by type, so a third channel is a compile error here rather than a
 * campaign that runs ungated.
 */
export const SEND_RISK_GATE_BY_CHANNEL: Readonly<Record<Channel, SendRiskGate>> = {
  email: EMAIL_SEND_RISK_GATE,
  whatsapp: WHATSAPP_SEND_RISK_GATE,
};

/** The one supported way to obtain a gate. There is no threshold parameter anywhere. */
export function sendRiskGateFor(channel: Channel): SendRiskGate {
  return SEND_RISK_GATE_BY_CHANNEL[channel];
}

/* ---- The measurement, which cannot carry the wrong channel's numerator ---- */

/**
 * What an **email** campaign has actually learned so far.
 *
 * `settled` is the denominator and the only denominator: distinct messages this
 * campaign has a terminal delivery outcome for — delivered, or bounced, or
 * rejected. `hardBounced` is the subset that bounced *permanently*, which is the
 * only kind AWS's reputation figure counts and the only kind that means the
 * address does not exist. A soft bounce is a full mailbox and must never be in
 * this numerator; the five not-delivered outcomes stay apart, as `delivery.ts`
 * insists.
 *
 * There is **no `accepted` field**, and its absence is the point — see the
 * header. Progress lives on `campaignProgressReading`.
 */
export const emailSendRiskMeasurementSchema = z
  .object({
    channel: z.literal('email'),
    /** countDistinct(message_id) with a terminal delivery outcome. */
    settled: z.number().int().min(0),
    /** countDistinct(message_id) behind a PERMANENT bounce. Never soft. */
    hardBounced: z.number().int().min(0),
  })
  .strict();
export type EmailSendRiskMeasurement = z.infer<typeof emailSendRiskMeasurementSchema>;

/**
 * What a **WhatsApp** campaign has actually learned so far.
 *
 * `undeliverable` counts distinct messages whose Meta failure class means the
 * number cannot receive messages at all — slice 12's
 * `WA_SUPPRESSING_FAILURE_CLASSES`, which is already the set this product treats
 * as permanent. A rate limit, a template pause or an unrecognised code is **not**
 * in it, for the reason the suppression rule gives: failing to count one costs a
 * slower ramp, and wrongly counting one halts a campaign that was fine.
 *
 * The field is not called `bounced` and the type has no field of that name, so
 * an email measurement cannot be passed where this one is expected.
 */
export const whatsAppSendRiskMeasurementSchema = z
  .object({
    channel: z.literal('whatsapp'),
    settled: z.number().int().min(0),
    /** countDistinct(message_id) whose failure class means the number cannot receive. */
    undeliverable: z.number().int().min(0),
  })
  .strict();
export type WhatsAppSendRiskMeasurement = z.infer<typeof whatsAppSendRiskMeasurementSchema>;

export const sendRiskMeasurementSchema = z.discriminatedUnion('channel', [
  emailSendRiskMeasurementSchema,
  whatsAppSendRiskMeasurementSchema,
]);
export type SendRiskMeasurement = z.infer<typeof sendRiskMeasurementSchema>;

/** The numerator, chosen by the measurement's own channel. Exhaustive. */
function failuresIn(measurement: SendRiskMeasurement): number {
  switch (measurement.channel) {
    case 'email':
      return measurement.hardBounced;
    case 'whatsapp':
      return measurement.undeliverable;
    default: {
      const exhaustive: never = measurement;
      throw new Error(`Unhandled send risk channel ${String(exhaustive)}`);
    }
  }
}

/**
 * The rate, as a {@link RateReading} that cannot be rendered from too small a
 * sample.
 *
 * Built with `rateReading` from `./overview.js` rather than by dividing here,
 * because there is one implementation of "is there enough volume for this
 * percentage to mean anything" and this file is not allowed to be the second.
 * The minimum passed is the **gate's** floor, not the Overview's 1,000: this is
 * a stop, not a headline, and the two answer different questions — see
 * {@link SendRiskGate.minimumSettledOutcomes}.
 *
 * A reading whose `rate` is `null` has not measured anything. It is never `0`,
 * and no caller may treat it as a pass — {@link mustCampaignHalt} does not halt
 * on it and {@link mayWaveRelease} does not release on it, which is the whole
 * shape of the third state.
 */
export function sendRiskRate(measurement: SendRiskMeasurement): RateReading {
  const gate = sendRiskGateFor(measurement.channel);
  return rateReading(failuresIn(measurement), measurement.settled, gate.minimumSettledOutcomes);
}

/* -------------------------------------------------------------------------- */
/* 3. THE AUTOMATIC HALT                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Why a campaign stopped itself.
 *
 *   `email_hard_bounce_rate`      too many permanent bounces, email only
 *   `whatsapp_undeliverable_rate` too many unreachable numbers, WhatsApp only
 *   `risk_unmeasurable`           the rate could not be computed at all
 *   `plan_unreadable`             the warm-up plan or its wave rows would not parse
 *
 * The last two are the fail-closed branch, and they are members rather than
 * exceptions because a campaign that stopped for an internal reason must be
 * distinguishable on screen from one that stopped because the list is bad. Those
 * need very different sentences and only one of them means "clean your list".
 *
 * **The two rate reasons are per channel and are selected from the gate**, so a
 * WhatsApp halt cannot be described in email's words.
 */
export const CAMPAIGN_HALT_REASON_VALUES = [
  'email_hard_bounce_rate',
  'whatsapp_undeliverable_rate',
  'risk_unmeasurable',
  'plan_unreadable',
] as const;
export const campaignHaltReasonSchema = z.enum(CAMPAIGN_HALT_REASON_VALUES);
export type CampaignHaltReason = z.infer<typeof campaignHaltReasonSchema>;

/**
 * Which rate reason belongs to which channel. Exhaustive over `Channel`, so a
 * third channel cannot silently inherit email's.
 */
export const HALT_RATE_REASON_BY_CHANNEL: Readonly<Record<Channel, CampaignHaltReason>> = {
  email: 'email_hard_bounce_rate',
  whatsapp: 'whatsapp_undeliverable_rate',
};

/**
 * The sentence for a halt read back from the database, which stores only the
 * code.
 *
 * Whole sentences, in the words a person who does not know what SES is can act
 * on, and each one ends with the thing to do. {@link campaignHaltSentence} is
 * the richer version used at the moment of halting, when the counts are in hand;
 * this is what the campaign screen renders afterwards.
 *
 * ## THE TWO RATE SENTENCES USED TO END WITH AN INSTRUCTION THAT DID NOTHING
 *
 * They said "clean the list, then resume the campaign", and cleaning the list
 * does not move this rate. The rate is `failures / settled` over the messages
 * this campaign has **already sent**; writing suppressions changes neither term.
 * What moves it is further outcomes arriving for messages already sent, which
 * grows the denominator — and `CampaignsService.resume` now refuses while the
 * rate is still at or above the line, so the old ending named the one action
 * that could not work and implied a button that would have been refused.
 *
 * Both halves are true and both are said: take the failing addresses off the
 * list, because that is what stops the *rest* of the campaign repeating it, and
 * the stop itself lifts only as more results come back.
 */
export const CAMPAIGN_HALT_SENTENCES: Readonly<Record<CampaignHaltReason, string>> = {
  email_hard_bounce_rate:
    'Wend stopped this campaign because too many of the addresses it sent to do not exist. Amazon reviews a sending account when its bounce rate climbs, and that account is yours, so sending stops before it gets there. Take the dead addresses off the list so the rest of the campaign does not repeat it. Resuming works once the rate has come down: it is measured over the messages already sent, so it moves as their results arrive.',
  whatsapp_undeliverable_rate:
    'Wend stopped this campaign because too many of the numbers it sent to cannot receive WhatsApp messages. Meta lowers a number’s quality rating when that keeps happening, which reduces how many messages you may send a day. Take the unreachable numbers off the list so the rest of the campaign does not repeat it. Resuming works once the rate has come down: it is measured over the messages already sent, so it moves as their results arrive.',
  risk_unmeasurable:
    'Wend stopped this campaign because it could not tell how the messages already sent were doing. Sending more without knowing that is the risk this check exists to avoid. Try resuming it; if it stops here again, the delivery reports are not reaching Wend.',
  plan_unreadable:
    'Wend stopped this campaign because its send-in-batches plan could not be read. Nothing further has been sent. Cancel the campaign and schedule it again to build a fresh plan.',
};

/** A halt, or the absence of one. Nothing in between. */
export type CampaignHaltDecision =
  | { readonly halt: false }
  | {
      readonly halt: true;
      readonly reason: CampaignHaltReason;
      /** A whole sentence, safe to show a customer, naming the counts where there are any. */
      readonly message: string;
    };

/**
 * **Must this campaign stop itself?**
 *
 * Called at every batch boundary in `campaign-send.runner.ts` — the boundary
 * that already re-reads the campaign — and again before any wave is released.
 *
 * Three branches, and the middle one is the one that makes this safe:
 *
 *   1. **no measurement at all** → halt, `risk_unmeasurable`. The fail-closed
 *      default, and its cost is real and accepted: a transient read failure
 *      stops a campaign that a person then has to resume. The alternative is
 *      sending the rest of a cold list while blind to what the first part did,
 *      which is the failure this slice exists to prevent. Stopping is cheap and
 *      reversible; an account review is neither.
 *   2. **measured, but under the sample floor** → do not halt. A rate over
 *      fewer than {@link SendRiskGate.minimumSettledOutcomes} outcomes cannot
 *      halt anything, because one failure would be enough to trip it. This is
 *      not a pass — {@link mayWaveRelease} refuses to release on the same
 *      reading — it is a refusal to *stop* on noise while continuing the
 *      current wave, which is already bounded in size.
 *   3. **measured, at or above the line** → halt. `>=`, not `>`: sitting exactly
 *      on the threshold is not a pass, the same rule `healthBand` applies to
 *      AWS's own line.
 *
 * It takes **no threshold argument**. The threshold comes from the measurement's
 * own channel, so there is no way to hand a WhatsApp campaign an email number.
 *
 * ## THE ONE WAY TO MISUSE THIS, STATED WHERE IT WILL BE READ
 *
 * `null` means **the rate could not be computed** — the query failed, the plan
 * would not parse, the campaign row could not be read. It does **not** mean
 * "this campaign has not sent anything yet". A campaign with nothing settled is
 * `{ channel, settled: 0, hardBounced: 0 }`, which answers `halt: false` through
 * branch 2 above.
 *
 * Passing `null` for the empty case would halt every campaign at its first batch
 * boundary, and it would do so with a sentence telling the reader their delivery
 * reports are not arriving. The caller owns that distinction; there is no way to
 * express it in this signature, which is why it is written here rather than
 * left to be discovered.
 */
export function mustCampaignHalt(
  measurement: SendRiskMeasurement | null,
): CampaignHaltDecision {
  if (measurement === null) {
    return {
      halt: true,
      reason: 'risk_unmeasurable',
      message: CAMPAIGN_HALT_SENTENCES.risk_unmeasurable,
    };
  }

  const gate = sendRiskGateFor(measurement.channel);
  const reading = sendRiskRate(measurement);

  if (reading.rate === null) return { halt: false };
  if (reading.rate < gate.haltRate) return { halt: false };

  const reason = HALT_RATE_REASON_BY_CHANNEL[measurement.channel];
  return { halt: true, reason, message: campaignHaltSentence(reason, reading) };
}

/**
 * The halt sentence with the counts in it.
 *
 * **The counts, not the percentage.** At the sample floor the denominator is 51
 * and "3.9%" is a number this codebase's own rules forbid rendering — *never
 * render a percentage from a denominator too small to mean anything, show the
 * counts and say why*. So the sentence says "3 of 51", which is true at any
 * volume, and the standing explanation from {@link CAMPAIGN_HALT_SENTENCES}
 * carries the reason.
 */
export function campaignHaltSentence(reason: CampaignHaltReason, reading: RateReading): string {
  const standing = CAMPAIGN_HALT_SENTENCES[reason];
  if (reason === 'risk_unmeasurable' || reason === 'plan_unreadable') return standing;
  const noun = reason === 'email_hard_bounce_rate' ? 'bounced permanently' : 'could not be reached';
  return `${count(reading.numerator)} of the ${count(reading.denominator)} messages we have results for ${noun}. ${standing}`;
}

/* -------------------------------------------------------------------------- */
/* 4. THE WARM-UP PLAN                                                         */
/* -------------------------------------------------------------------------- */

/**
 * How much of a wave has to have reported back before its rate is trusted.
 *
 * Half. The reasoning is a bound rather than a taste: at 50% coverage the part
 * still unknown is no larger than the part measured, so the observed rate cannot
 * be hiding a population bigger than the one it was computed over. Below that,
 * the silent majority can be anything, and a clean-looking rate over a fifth of
 * a wave is the *"the timer elapsed so it must be fine"* mistake with a number
 * attached.
 *
 * This is a **second, independent** gate to the sample floor. The floor asks
 * "are there enough outcomes for a rate to mean anything"; this asks "have
 * enough of *this campaign's* messages answered". A 5,000-message wave clears
 * the floor after 51 outcomes and has learned almost nothing.
 */
export const WAVE_SETTLED_SHARE_REQUIRED = 0.5;

/**
 * The seed: the **smallest wave whose settled half is still a measurable
 * sample**.
 *
 * `ceil(51 / 0.5)` = 102. Derived, and the derivation is the argument: a seed
 * too small to measure is not a safety device, it is a ritual. A seed of 20
 * finishes, reports two bounces, and cannot legitimately conclude anything from
 * them — so the campaign either releases on noise or holds forever. Either way
 * the seed did no work.
 *
 * It is derived from the **email** gate, which has the larger floor, because
 * there is one plan shape and it has to be measurable on the stricter channel.
 * On WhatsApp the same 102 clears that channel's floor of 21 comfortably.
 *
 * 102 of 1,278 is 8% of the real list on the first shot, and that is the honest
 * cost of being able to measure at all.
 */
export const SEED_WAVE_SIZE = Math.ceil(
  EMAIL_SEND_RISK_GATE.minimumSettledOutcomes / WAVE_SETTLED_SHARE_REQUIRED,
);

/**
 * Below this many recipients there is **no warm-up plan at all**.
 *
 * `SEED_WAVE_SIZE * 2` — a plan needs at least two waves, because one wave is
 * just a send with extra vocabulary. A three-contact test segment dragged
 * through a seed, a sixty-minute hold and a release gate is pure friction, and
 * friction is what teaches somebody to schedule around the feature.
 *
 * **The floor skips the waves. It does not skip the halt — but the halt has a
 * floor of its own, and the two are not the same number.**
 *
 * {@link mustCampaignHalt} takes no plan and no size, so it is *asked* at every
 * volume. What it can *answer* is bounded by
 * {@link SendRiskGate.minimumSettledOutcomes}: below that many settled
 * outcomes the rate is `null` and nothing halts, because one failure would
 * otherwise be enough to trip the line. On email that floor is 51.
 *
 * So the honest statement of this floor's safety is narrower than it looks, and
 * it is written out rather than left to be discovered:
 *
 *   * a list **between 51 and 203** gets no waves and is still covered — it can
 *     accumulate enough settled outcomes for the halt to fire, and it does;
 *   * a list of **50 or fewer** can never settle 51 outcomes, so
 *     {@link mustCampaignHalt} can never halt it however dead the list is. It
 *     is covered by {@link listHealth}'s advice and by nothing else.
 *
 * That hole is deliberate and it is cheap: fifty cold addresses cannot move an
 * account's rolling rate on their own, which is the harm this file exists to
 * prevent. It is stated here because the superseded version of this paragraph
 * claimed the opposite — *"a 50-recipient campaign to a dead list still stops
 * itself once two of its first outcomes bounce"* — which is arithmetically
 * impossible at a floor of 51, and a false reassurance about a small list is
 * exactly the argument somebody uses to lower this constant.
 */
export const WARM_UP_MINIMUM_LIST_SIZE = SEED_WAVE_SIZE * 2;

/** How many waves at most, whatever the size of the list. */
export const MAX_WARM_UP_WAVES = 6;

/**
 * How fast a plan may grow, per verdict from defence 1. **This is where the
 * health check drives the plan.**
 *
 * A clean-looking list quadruples: 102 → 408 → 1,632. A list the check is
 * worried about doubles, so there are more decision points between the seed and
 * the bulk of the send, and each one is a chance for the halt to fire before the
 * next multiple of the damage.
 */
export const WAVE_GROWTH_BY_VERDICT: Readonly<Record<ListHealthVerdict, number>> = {
  ready: 4,
  warm_up_advised: 4,
  high_risk: 2,
  do_not_send: 2,
};

/**
 * How long to hold after the seed, per verdict.
 *
 * An hour is the floor for every list, and it is not arbitrary: a hard bounce is
 * often *not* immediate. A receiving MTA that accepts and then rejects gives SES
 * an asynchronous bounce minutes to hours later, so a fifteen-minute hold
 * measures the fast failures and misses the slow ones — and the slow ones are
 * the dead mailboxes at an organisation whose mail server accepts everything at
 * the edge. On a first campaign, an hour is the cheapest hour in the schedule.
 *
 * The worried verdicts hold longer because the thing they are worried about is
 * exactly the slow bounce.
 */
export const SEED_HOLD_MINUTES_BY_VERDICT: Readonly<Record<ListHealthVerdict, number>> = {
  ready: 60,
  warm_up_advised: 60,
  high_risk: 120,
  do_not_send: 240,
};

/**
 * How long to hold after every wave after the seed.
 *
 * Shorter than the seed's hold, and that is not impatience: by wave three the
 * denominator behind the rate is the whole campaign so far, not one wave, so the
 * measurement is already strong before the clock starts. The hold is a floor on
 * *arrival time*, and {@link WAVE_SETTLED_SHARE_REQUIRED} is what actually
 * decides whether enough has arrived.
 */
export const WAVE_HOLD_MINUTES = 30;

/**
 * When a hold with **no delivery reports at all** starts saying something
 * stronger.
 *
 * It changes the sentence and never the verdict. Three hours of silence usually
 * means the SNS event destination is not wired up rather than that the mail is
 * in flight, and that is a fact a person has to act on — but a hold that
 * *released* itself after three hours would be releasing on a denominator of
 * zero, which is the exact thing this file exists to refuse.
 */
export const UNSETTLED_HOLD_ESCALATES_AFTER_MINUTES = 180;

/** One wave of a plan: how many, and how long to wait afterwards. */
export const warmUpWaveSchema = z
  .object({
    /** 1-based. Wave 1 is the seed. */
    waveNumber: z.number().int().min(1),
    plannedSize: z.number().int().min(1),
    /** Minimum minutes between this wave finishing and the next being allowed to start. */
    holdMinutes: z.number().int().min(0),
  })
  .strict();
export type WarmUpWave = z.infer<typeof warmUpWaveSchema>;

/**
 * The plan: an ordered list of waves, and the verdict that produced it.
 *
 * `seedSelection` is a literal rather than an enum with one member for the
 * reason `segment_type` is an enum with one member: it is the extension point,
 * and naming it here is what stops a second selection strategy appearing as an
 * unnamed `if` in the runner. Today there is exactly one, and it is
 * {@link seedByGroupSpread}.
 */
export const warmUpPlanSchema = z
  .object({
    channel: channelSchema,
    verdict: listHealthVerdictSchema,
    waves: z.array(warmUpWaveSchema).min(1),
    /** How the seed's members are chosen. See {@link seedByGroupSpread}. */
    seedSelection: z.literal('group_spread'),
    /** Recipients the plan was built for. A plan that no longer covers the list is stale. */
    plannedForRecipients: z.number().int().min(1),
  })
  .strict();
export type WarmUpPlan = z.infer<typeof warmUpPlanSchema>;

/**
 * Does a list this size get a plan at all?
 *
 * Exported separately from {@link warmUpPlanFor} so the wizard can say *"this
 * list is small enough to send in one go"* without constructing a plan it is
 * about to discard.
 */
export function needsWarmUp(recipientCount: number): boolean {
  return recipientCount >= WARM_UP_MINIMUM_LIST_SIZE;
}

/**
 * The plan for a list, or `null` when it is too small to need one.
 *
 * Seed, then multiply by the verdict's growth factor, capped at
 * {@link MAX_WARM_UP_WAVES}, with the **last wave taking the whole remainder**
 * however large it is. That last rule is deliberate and it is a limitation
 * stated rather than hidden: a 100,000-address list finishes with one wave of
 * 90,000. By then five waves have been measured and cleared, and the campaign
 * halts mid-wave anyway if the rate turns — the wave boundary is not the only
 * gate, and pretending it is would produce a fourteen-wave plan nobody would
 * wait for.
 *
 * **A `ready` verdict still gets a plan.** The health check is offline and
 * cannot tell a live address from a dead one, so "nothing in the data argues
 * against sending" is not evidence that the addresses exist. That is the whole
 * reason defence 2 is not a branch of defence 1.
 */
export function warmUpPlanFor(input: {
  readonly channel: Channel;
  readonly recipientCount: number;
  readonly verdict: ListHealthVerdict;
}): WarmUpPlan | null {
  const total = Math.max(0, Math.trunc(input.recipientCount));
  if (!needsWarmUp(total)) return null;

  const growth = WAVE_GROWTH_BY_VERDICT[input.verdict];
  const waves: WarmUpWave[] = [];
  let remaining = total;
  let size = SEED_WAVE_SIZE;

  while (remaining > 0 && waves.length < MAX_WARM_UP_WAVES) {
    const isLast = waves.length === MAX_WARM_UP_WAVES - 1;
    // The last permitted wave takes everything left; any other takes its planned
    // size, or the remainder when the remainder is smaller.
    const plannedSize = isLast ? remaining : Math.min(size, remaining);
    const waveNumber = waves.length + 1;
    waves.push({
      waveNumber,
      plannedSize,
      holdMinutes: waveNumber === 1 ? SEED_HOLD_MINUTES_BY_VERDICT[input.verdict] : WAVE_HOLD_MINUTES,
    });
    remaining -= plannedSize;
    size = size * growth;
  }

  return {
    channel: input.channel,
    verdict: input.verdict,
    waves,
    seedSelection: 'group_spread',
    plannedForRecipients: total,
  };
}

/* ---- The seed has to be representative, and "first N" is not -------------- */

/**
 * The key a list clusters on: the email domain, or the WhatsApp number's
 * country-and-operator prefix.
 *
 * A phone list clusters the way an email list does — `+9198…` numbers are one
 * operator in one country, and a seed drawn entirely from one prefix proves
 * nothing about another. Five characters is `+` plus a country code plus the
 * first digits of the mobile prefix, which is the granularity at which Indian
 * numbers actually separate.
 *
 * Falls back to the whole value rather than to `null`: an address with no domain
 * is its own group of one, which spreads it rather than dropping it.
 */
export function spreadGroupFor(channel: Channel, value: string): string {
  const normalised = value.trim().toLowerCase();
  if (channel === 'email') return emailDomain(normalised) ?? normalised;
  const prefix = normalised.slice(0, 5);
  return prefix === '' ? normalised : prefix;
}

/**
 * **The seed, spread deliberately across groups.**
 *
 * ## Why the obvious implementation is wrong
 *
 * "The first 50 by id" is import order. On this database, import order is the
 * order of a spreadsheet exported from a summit registration, which is
 * **alphabetical by bank name** — so it is also very nearly grouped by domain,
 * because a cooperative bank's address is usually at its own domain and its
 * branches share it. A seed taken off the front is therefore a seed of one or
 * two domains. It comes back clean and tells you nothing about the other
 * 1,176 people; or it comes back terrible and condemns a list that was fine
 * apart from one dead server. Both outcomes look like a measurement and neither
 * is one.
 *
 * Ordering by `random()` fixes the clustering and breaks reproducibility: two
 * calls disagree about who is in the seed, and a seed nobody can reconstruct is
 * a seed nobody can explain afterwards.
 *
 * ## What this does instead
 *
 * Buckets by {@link spreadGroupFor}, orders the buckets largest-first with the
 * key as a tie-break, sorts within each bucket, and then takes **one from each
 * bucket in turn** until the seed is full. Deterministic: the same candidates in
 * any order produce the same seed.
 *
 * Round-robin rather than proportional sampling, and that is a real choice. A
 * proportional seed would reproduce the concentration the health check has
 * already reported — 60% of the seed on the domain that is 60% of the list —
 * and the seed's job is not to predict the aggregate, it is to find out whether
 * the list is bad *anywhere*. Breadth finds that; proportion measures the thing
 * we already know.
 */
/**
 * **How much bigger than the wave its candidate pool is allowed to be.**
 *
 * {@link seedByGroupSpread} is pure and needs its candidates in memory, and the
 * superseded implementation of `waveMembers` therefore read *every* `pending`
 * recipient of the campaign on every wave release. At 1,278 rows that is
 * nothing; at `MAX_UPLOAD_ROWS` it is 200,000 four-column rows in one process's
 * heap, re-read for each of six waves, to choose 102 of them for the seed.
 *
 * The obvious bound is `LIMIT size`, and it is **exactly wrong**: the pool has
 * to be ordered by something, every ordering available is address or import
 * order, and a prefix of that order is the alphabetical-by-bank-name clustering
 * this whole selection exists to break. A cheap seed of one domain is worse than
 * an expensive seed of thirty.
 *
 * So the bound is applied to a pool that has already been **ranked within its
 * group** in SQL — take every group's first member, then every group's second,
 * and so on — and then cut at `size × this`. Every prefix of that ordering is a
 * whole number of round-robin rounds, so the bounded pool spans exactly as many
 * groups as the unbounded one, and it always holds at least `size` rows whenever
 * the campaign has that many left. The spread itself is still computed here,
 * from the pool, so there is one implementation of the rule and SQL only decides
 * which candidates it is allowed to consider.
 *
 * Two rather than a larger multiple because the round-robin needs no more than
 * one spare round to keep its bucket ordering meaningful, and the cost is linear
 * in it. The last wave of a plan takes the whole remainder by design, so its
 * pool is still the whole remainder — releasing *n* recipients has to read *n*
 * rows, and no bound can change that. What this removes is the 200,000-row read
 * behind a 102-row seed.
 */
export const SEED_POOL_MULTIPLE = 2;

export function seedByGroupSpread<T>(
  candidates: readonly T[],
  channel: Channel,
  addressOf: (item: T) => string,
  size: number,
): T[] {
  const limit = Math.max(0, Math.trunc(size));
  if (limit === 0) return [];

  const buckets = new Map<string, { key: string; items: { address: string; item: T }[] }>();
  for (const item of candidates) {
    const address = addressOf(item).trim().toLowerCase();
    const key = spreadGroupFor(channel, address);
    const bucket = buckets.get(key) ?? { key, items: [] };
    bucket.items.push({ address, item });
    buckets.set(key, bucket);
  }

  const ordered = [...buckets.values()].sort(
    (a, b) => b.items.length - a.items.length || a.key.localeCompare(b.key),
  );
  for (const bucket of ordered) {
    bucket.items.sort((a, b) => a.address.localeCompare(b.address));
  }

  const seed: T[] = [];
  for (let round = 0; seed.length < limit; round += 1) {
    let tookOne = false;
    for (const bucket of ordered) {
      const entry = bucket.items[round];
      if (entry === undefined) continue;
      seed.push(entry.item);
      tookOne = true;
      if (seed.length >= limit) break;
    }
    // Every bucket is exhausted: the candidate list was smaller than the seed.
    if (!tookOne) break;
  }
  return seed;
}

/* -------------------------------------------------------------------------- */
/* 5. WAVE STATE, AND THE RELEASE GATE                                         */
/* -------------------------------------------------------------------------- */

/**
 * Where one wave has got to.
 *
 *   `pending`   planned, nothing released
 *   `releasing` claimed by a run; its recipients are being handed to a provider
 *   `sent`      every recipient of this wave has been attempted; the hold clock started
 *   `cleared`   the hold elapsed and the rate was acceptable; the next wave may start
 *
 * **There is no `halted` member, deliberately.** A halt is a property of the
 * *campaign*, not of a wave: the wave that was running when the campaign halted
 * genuinely did finish being sent, and marking it `halted` would lose that. A
 * user must never be shown a holding state described as a halted one, and the
 * cleanest way to guarantee that is for the two never to be values of the same
 * column.
 *
 * `releasing` is what makes a release happen exactly once — see
 * {@link WAVE_CLAIM_IS_A_CONDITIONAL_UPDATE}.
 */
export const CAMPAIGN_WAVE_STATUS_VALUES = ['pending', 'releasing', 'sent', 'cleared'] as const;
export const campaignWaveStatusSchema = z.enum(CAMPAIGN_WAVE_STATUS_VALUES);
export type CampaignWaveStatus = z.infer<typeof campaignWaveStatusSchema>;

/**
 * **How a wave releases exactly once.** Stated as a constant so it is greppable
 * from both sides of the seam, and so the reasoning cannot be separated from the
 * rule.
 *
 * The obvious bug is two scheduler ticks releasing the same wave. The obvious
 * fix — `SELECT … FOR UPDATE` on the wave row — **does not work here**, and the
 * reason is the single most expensive lesson in this codebase: `withTenant`
 * opens a transaction and commits it when the callback returns, so a lock taken
 * inside its own `TenantDbService.run()` is released the instant that callback
 * ends, long before anything it was protecting happens. Slice 6 shipped exactly
 * that and the lock annulled itself.
 *
 * So the claim is a **conditional UPDATE naming the state it is leaving**:
 *
 * ```sql
 * UPDATE campaign_send_waves
 *    SET status = 'releasing', released_at = now()
 *  WHERE campaign_id = $1 AND wave_number = $2 AND status = 'pending'
 * RETURNING id
 * ```
 *
 * One statement is atomic against itself whatever the transaction around it
 * does. The loser gets zero rows back and stops. No row returned means no claim,
 * and `RETURNING` is what makes that observable rather than inferred from a
 * rowcount somebody forgot to check.
 *
 * `campaign_send_waves_one_releasing` — a partial unique index on `campaign_id`
 * where `status = 'releasing'` — is the second, independent guarantee: even a
 * hand-written UPDATE from psql cannot put two waves of one campaign in flight.
 */
export const WAVE_CLAIM_IS_A_CONDITIONAL_UPDATE =
  "A wave is claimed by UPDATE … SET status = 'releasing' WHERE status = 'pending' RETURNING id — never by SELECT … FOR UPDATE, which releases when withTenant commits.";

/**
 * One wave as the API reports it.
 *
 * A discriminated union on `status`, so the impossible rows cannot be
 * constructed: a `pending` wave that claims a release time, a `sent` wave that
 * cannot say how many it sent, a `cleared` wave with no clock behind it. Same
 * instrument `reportRunSchema` uses, for the same reason — the CHECKs in the
 * migration say it at the database, and this says it on the wire.
 */
export const campaignWaveSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('pending'),
      waveNumber: z.number().int().min(1),
      plannedSize: z.number().int().min(1),
      holdMinutes: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      status: z.literal('releasing'),
      waveNumber: z.number().int().min(1),
      plannedSize: z.number().int().min(1),
      holdMinutes: z.number().int().min(0),
      releasedAt: z.string(),
    })
    .strict(),
  z
    .object({
      status: z.literal('sent'),
      waveNumber: z.number().int().min(1),
      plannedSize: z.number().int().min(1),
      holdMinutes: z.number().int().min(0),
      releasedAt: z.string(),
      /** When the last recipient of this wave was attempted. The hold clock starts here. */
      sentAt: z.string(),
      /** How many were actually attempted, which can be fewer than planned. */
      releasedCount: z.number().int().min(0),
    })
    .strict(),
  z
    .object({
      status: z.literal('cleared'),
      waveNumber: z.number().int().min(1),
      plannedSize: z.number().int().min(1),
      holdMinutes: z.number().int().min(0),
      releasedAt: z.string(),
      sentAt: z.string(),
      releasedCount: z.number().int().min(0),
      clearedAt: z.string(),
    })
    .strict(),
]);
export type CampaignWave = z.infer<typeof campaignWaveSchema>;

/**
 * When a finished wave's hold expires, or `null` when the clock has not started.
 *
 * Derived, never stored. `sent_at + hold_minutes` in the database would be a
 * second source of truth for a value that is a subtraction, and `timestamptz +
 * interval` is only STABLE in Postgres, so it cannot be a generated column
 * anyway — the constraint and the principle agree for once.
 */
export function waveHoldUntil(wave: CampaignWave): Date | null {
  if (wave.status !== 'sent' && wave.status !== 'cleared') return null;
  const sentAt = Date.parse(wave.sentAt);
  if (Number.isNaN(sentAt)) return null;
  return new Date(sentAt + wave.holdMinutes * 60_000);
}

/**
 * Why the next wave is not going yet.
 *
 * `plan_exhausted` is the one member that describes a hold **that never ends on
 * its own**, and it exists because the two states it separates were being told
 * to the reader as one. A segment re-materialised underneath a scheduled
 * campaign leaves more recipients than the committed waves cover: the last wave
 * clears, `closeWave` sees rows still `pending` and moves the campaign to
 * `holding_between_waves`, and there is no `pending` wave for the gate to
 * release. Every other member here is a *wait* — more reports will arrive, the
 * clock will run out — and describing this one as *"waiting for more delivery
 * reports"* tells somebody to sit still in front of a campaign that will sit
 * still with them for ever. A stale plan still holds rather than inventing a
 * wave boundary underneath a part-sent campaign; what changes is that it says
 * so.
 */
export const WAVE_HOLD_REASON_VALUES = [
  'hold_time_not_elapsed',
  'no_delivery_reports_yet',
  'too_few_delivery_reports',
  'rate_at_or_above_threshold',
  'campaign_not_holding',
  'plan_exhausted',
  'unreadable',
] as const;
export const waveHoldReasonSchema = z.enum(WAVE_HOLD_REASON_VALUES);
export type WaveHoldReason = z.infer<typeof waveHoldReasonSchema>;

/** Release, or hold with a reason a person can read. Never a bare boolean. */
export type WaveReleaseDecision =
  | { readonly release: true }
  | {
      readonly release: false;
      readonly reason: WaveHoldReason;
      /** A whole sentence, safe to show a customer. */
      readonly message: string;
      /** When the time half of the gate opens. Null when time is not what is holding it. */
      readonly holdUntil: string | null;
    };

/** Everything {@link mayWaveRelease} needs, and nothing it cannot have. */
export interface WaveReleaseContext {
  /** The campaign's status right now. Only a holding campaign may release a wave. */
  readonly campaignStatus: CampaignStatus;
  /**
   * The wave that has already been sent. `null` means the plan or its rows could
   * not be read, which holds — it does not mean "this is the seed". The seed is
   * released by the schedule path, not by this gate.
   */
  readonly previousWave: CampaignWave | null;
  /** What the campaign has learned so far. `null` when it could not be computed. */
  readonly measurement: SendRiskMeasurement | null;
  /** Messages handed to a provider so far, for the coverage test. */
  readonly handedToProvider: number;
  /** Injected so the function stays pure and testable. Defaults to now. */
  readonly now?: Date;
}

/**
 * **May the next wave release?**
 *
 * Both halves of the gate, and both must pass: the hold time has elapsed **and**
 * the observed rate is under the line — with a third state between them that is
 * neither.
 *
 * The order of the branches is the argument:
 *
 *   1. the campaign is not in a holding state → hold. A halted campaign, a
 *      cancelled one, one still draining its current wave: none of them releases
 *      anything, and reaching this function in any of those states is a caller
 *      bug rather than a decision.
 *   2. **the plan is unreadable** → hold. The fail-closed default, written first
 *      rather than last.
 *   3. the hold time has not elapsed → hold, with the instant it opens.
 *   4. **no delivery reports at all** → hold. THE THIRD STATE, and it is neither
 *      "fine" nor "bad". A hold that only watched the clock has learned nothing:
 *      bounces arrive by SNS minutes to hours after the send, so a wave that
 *      finished five minutes ago and has zero events is not a clean wave, it is
 *      an unanswered one. Releasing on a denominator that has not settled is the
 *      precise failure this slice exists to prevent, so this state never
 *      releases — it changes its **sentence** after
 *      {@link UNSETTLED_HOLD_ESCALATES_AFTER_MINUTES}, never its verdict,
 *      because three hours of silence is a fact a person has to act on and not a
 *      reason to send more.
 *   5. fewer than {@link WAVE_SETTLED_SHARE_REQUIRED} of the messages sent have
 *      answered → hold. Coverage, which is a different question from the sample
 *      floor.
 *   6. the rate is at or above the line → hold. In practice
 *      {@link mustCampaignHalt} will already have halted the campaign at this
 *      rate; this branch is what makes the gate correct on its own rather than
 *      correct because something else ran first.
 *   7. otherwise → release.
 *
 * Note what is **not** a release condition: a measured rate of zero over a
 * denominator under the sample floor. That reading's `rate` is `null`, branch 5
 * or 6 catches it, and there is deliberately no branch that treats "not
 * measured" as "fine".
 */
export function mayWaveRelease(ctx: WaveReleaseContext): WaveReleaseDecision {
  const now = ctx.now ?? new Date();

  if (ctx.campaignStatus !== 'holding_between_waves') {
    return {
      release: false,
      reason: 'campaign_not_holding',
      message: WAVE_HOLD_SENTENCES.campaign_not_holding,
      holdUntil: null,
    };
  }

  const previous = ctx.previousWave;
  if (previous === null || (previous.status !== 'sent' && previous.status !== 'cleared')) {
    return {
      release: false,
      reason: 'unreadable',
      message: WAVE_HOLD_SENTENCES.unreadable,
      holdUntil: null,
    };
  }

  const holdUntil = waveHoldUntil(previous);
  if (holdUntil === null) {
    return {
      release: false,
      reason: 'unreadable',
      message: WAVE_HOLD_SENTENCES.unreadable,
      holdUntil: null,
    };
  }

  if (now.getTime() < holdUntil.getTime()) {
    return {
      release: false,
      reason: 'hold_time_not_elapsed',
      message: WAVE_HOLD_SENTENCES.hold_time_not_elapsed,
      holdUntil: holdUntil.toISOString(),
    };
  }

  const measurement = ctx.measurement;
  if (measurement === null) {
    return {
      release: false,
      reason: 'unreadable',
      message: WAVE_HOLD_SENTENCES.unreadable,
      holdUntil: null,
    };
  }

  if (measurement.settled === 0) {
    const silentForMinutes = Math.max(0, (now.getTime() - Date.parse(previous.sentAt)) / 60_000);
    return {
      release: false,
      reason: 'no_delivery_reports_yet',
      message:
        silentForMinutes >= UNSETTLED_HOLD_ESCALATES_AFTER_MINUTES
          ? // The measurement carries its own channel, so the escalated sentence
            // names the provider that is actually silent rather than Amazon on
            // both. See the constant's header.
            waveHoldSentenceNoReports(measurement.channel)
          : WAVE_HOLD_SENTENCES.no_delivery_reports_yet,
      holdUntil: null,
    };
  }

  const required = Math.ceil(Math.max(0, ctx.handedToProvider) * WAVE_SETTLED_SHARE_REQUIRED);
  if (measurement.settled < required) {
    return {
      release: false,
      reason: 'too_few_delivery_reports',
      message: WAVE_HOLD_SENTENCES.too_few_delivery_reports,
      holdUntil: null,
    };
  }

  const gate = sendRiskGateFor(measurement.channel);
  const reading = sendRiskRate(measurement);
  if (reading.rate === null) {
    return {
      release: false,
      reason: 'too_few_delivery_reports',
      message: WAVE_HOLD_SENTENCES.too_few_delivery_reports,
      holdUntil: null,
    };
  }
  if (reading.rate >= gate.haltRate) {
    return {
      release: false,
      reason: 'rate_at_or_above_threshold',
      message: WAVE_HOLD_SENTENCES.rate_at_or_above_threshold,
      holdUntil: null,
    };
  }

  return { release: true };
}

/* -------------------------------------------------------------------------- */
/* 6. THE SENTENCES — every user-visible string in this slice is here          */
/* -------------------------------------------------------------------------- */

/**
 * The default sentence for each hold reason.
 *
 * They live here rather than in a component for the reason the whole file lives
 * here: the API writes one of these onto a campaign and the screen renders it,
 * and two copies of a sentence is two products' worth of wording drifting apart.
 * Short by default, per the Shipping Reach text rule — the reasoning that
 * produced each one is in this file's header and is deleted from the UI.
 */
export const WAVE_HOLD_SENTENCES: Readonly<Record<WaveHoldReason, string>> = {
  hold_time_not_elapsed:
    'Waiting before the next batch, so there is time for problems with the first one to show up.',
  no_delivery_reports_yet:
    'Waiting for the first delivery reports. Nothing has come back yet, so there is nothing to judge the last batch on.',
  too_few_delivery_reports:
    'Waiting for more delivery reports. Too few messages have been reported on to tell how the last batch went.',
  rate_at_or_above_threshold:
    'Holding: too many of the messages already sent came back as failures. Nothing more will go out until somebody looks.',
  campaign_not_holding: 'This campaign is not waiting between batches.',
  // The one sentence here that is not about waiting. It names the action,
  // because nothing this campaign can do on its own will move it.
  plan_exhausted:
    'Holding: this campaign’s batches have all been sent, but more people were added to its list afterwards, so there is no batch left to send them in. It will not continue on its own — cancel it and schedule it again to reach them.',
  unreadable:
    'Holding: Wend could not read this campaign’s batch plan, so it will not send any more of it.',
};

/**
 * What a three-hour silence says instead, **per channel**.
 *
 * The verdict is identical — still holding — and only the sentence changes,
 * because the reader's next action changes: at five minutes there is nothing to
 * do but wait, and at three hours the likely explanation is that delivery
 * reports are not reaching Wend at all.
 *
 * ## WHY THIS IS A `Record<Channel, …>` AND WAS NOT
 *
 * It was one string for both channels, and it named **Amazon's notifications**.
 * Every other user-visible table in this file is exhaustive over `Channel`
 * precisely so that email's words cannot be drawn on a Meta campaign — and this
 * constant slipped past all of them, because {@link WAVE_HOLD_SENTENCES} is
 * keyed on the hold *reason*, which is channel-free by design, and this is the
 * one member of that family that names a provider.
 *
 * The state is reachable on WhatsApp and is not exotic: a wave finishes, Meta's
 * status callbacks do not arrive, three hours pass, and the screen told a tenant
 * sending through Meta to go and look at an Amazon integration they do not have.
 * The `Record` is the same instrument the rest of the file uses; a noun
 * substitution was rejected here for the reason
 * `LIST_HEALTH_SENTENCES_BY_CHANNEL` gives — the defect is a *claim* about which
 * provider is silent, not a noun.
 *
 * What is actually silent differs, and each sentence names the right thing: SES
 * reports arrive as SNS notifications, Meta's as status callbacks on the webhook.
 * Both sentences end the same way, because the verdict is the same on both.
 */
export const WAVE_HOLD_SENTENCE_NO_REPORTS_ESCALATED_BY_CHANNEL: Readonly<Record<Channel, string>> = {
  email:
    'No delivery reports have arrived for several hours. That usually means Amazon’s notifications are not reaching Wend rather than that the messages are still in flight. Sending stays paused until they do.',
  whatsapp:
    'No delivery reports have arrived for several hours. That usually means Meta’s status callbacks are not reaching Wend rather than that the messages are still in flight. Sending stays paused until they do.',
};

/** The only supported way to obtain it. There is no channel-free version. */
export function waveHoldSentenceNoReports(channel: Channel): string {
  return WAVE_HOLD_SENTENCE_NO_REPORTS_ESCALATED_BY_CHANNEL[channel];
}

/**
 * The two new campaign statuses, in the words a person reads.
 *
 * They are exported from here rather than written into `campaign-display.ts`
 * because a holding campaign and a halted one must never be described as each
 * other, and the surest way to keep two sentences distinct is to keep them
 * beside each other in the file that defines the difference.
 */
export const HOLDING_BETWEEN_WAVES_MEANING =
  'Part of this campaign has been sent and the rest is waiting. Wend sends a large list in batches and pauses between them to see how the last batch was received. Nobody has been skipped, and it continues on its own.';

export const HALTED_DELIVERY_RISK_MEANING =
  'Wend stopped this campaign part-way because of how the messages already sent were received. Everyone not yet reached is still waiting rather than skipped. It will not continue until somebody resumes it.';

/**
 * What the verdict says on the schedule screen, short first and detail on
 * demand.
 *
 * Progressive, per the text rule, and progressive does **not** mean deleted —
 * slice 16 applied that rule backwards once and removed an instruction
 * entirely. `summary` is what a skimmer reads; `detail` is what opens.
 */
export interface ListHealthVerdictCopy {
  readonly label: string;
  readonly summary: string;
  readonly detail: string;
}

/**
 * The verdict copy, **keyed on channel first**.
 *
 * The superseded table was `Record<ListHealthVerdict, …>` and the panel rendered
 * it for whatever campaign it was on. Two of the four details are email's and
 * only email's: `high_risk` said *"bounces count against your own Amazon
 * account… a high enough rate gets that account reviewed"*, and `do_not_send`
 * said *"a reviewed Amazon account affects every email that account sends"*. On
 * the real WhatsApp data — 752 numbers, none ever messaged, near-total prefix
 * concentration — the verdict is one of exactly those two, so a tenant sending
 * through Meta was told to worry about an Amazon account that has nothing to do
 * with the send.
 *
 * `ready.detail` was wrong in the other direction: it listed *"spelling… and how
 * many share one domain"*, and on WhatsApp `syntacticallyInvalid` and
 * `roleAddresses` are structurally zero — no spelling check runs at all — while
 * the group is a dialling prefix rather than a domain. A verdict that names
 * checks it did not perform is worse than one that names none.
 *
 * This file's own header cites the AWS-threshold-across-channels defect as the
 * reason `WHATSAPP_UNDELIVERABLE_HALT_RATE` is not derived from the email one.
 * The thresholds were kept apart and the copy was not; this is the copy half.
 * Exhaustive over `Channel`, so a third channel is a compile error rather than a
 * screen quietly inheriting email's words.
 */
export const LIST_HEALTH_VERDICT_COPY_BY_CHANNEL: Readonly<
  Record<Channel, Readonly<Record<ListHealthVerdict, ListHealthVerdictCopy>>>
> = {
  email: {
    ready: {
      label: 'Looks fine',
      summary: 'Nothing about this list stands out as a problem.',
      detail:
        'Wend checked the addresses it already holds for spelling, unsubscribes, past bounces, duplicates and how many share one domain, and found nothing unusual. It still sends a large list in batches, because none of those checks can tell whether an address is actually in use.',
    },
    warm_up_advised: {
      label: 'Send in batches',
      summary: 'A few things are worth knowing before this goes out.',
      detail:
        'None of the findings below stops a send on its own. Wend will send this list in batches and wait between them, so a problem shows up after the first hundred messages rather than after all of them.',
    },
    high_risk: {
      label: 'Risky list',
      summary: 'This list has a problem that usually produces bounces.',
      detail:
        'Bounces count against your own Amazon account, not against Wend, and a high enough rate gets that account reviewed. The batches below are smaller and the waits longer, and sending stops by itself if the first batch goes badly.',
    },
    do_not_send: {
      label: 'Do not send yet',
      summary: 'Several things about this list argue against sending it as it is.',
      detail:
        'You can still schedule this, and Wend will send it in small batches with long waits and stop at the first sign of trouble. Fixing the findings below first is the cheaper option: a reviewed Amazon account affects every email that account sends, not just this campaign.',
    },
  },
  whatsapp: {
    ready: {
      label: 'Looks fine',
      summary: 'Nothing about this list stands out as a problem.',
      detail:
        'Wend checked the numbers it already holds for opt-outs, numbers that have failed before, duplicates and how many share one dialling range, and found nothing unusual. It still sends a large list in batches, because none of those checks can tell whether a number is on WhatsApp at all.',
    },
    warm_up_advised: {
      label: 'Send in batches',
      summary: 'A few things are worth knowing before this goes out.',
      detail:
        'None of the findings below stops a send on its own. Wend will send this list in batches and wait between them, so a problem shows up after the first hundred messages rather than after all of them.',
    },
    high_risk: {
      label: 'Risky list',
      summary: 'This list has a problem that usually produces failed messages.',
      detail:
        'Numbers that cannot receive WhatsApp count against your own number’s quality rating with Meta, and a low enough rating cuts how many messages you may send a day. The batches below are smaller and the waits longer, and sending stops by itself if the first batch goes badly.',
    },
    do_not_send: {
      label: 'Do not send yet',
      summary: 'Several things about this list argue against sending it as it is.',
      detail:
        'You can still schedule this, and Wend will send it in small batches with long waits and stop at the first sign of trouble. Fixing the findings below first is the cheaper option: a quality rating affects every message that number sends, not just this campaign.',
    },
  },
};

/**
 * The one supported way to obtain verdict copy. There is no channel-free
 * version, which is what makes the defect above unrepresentable rather than
 * merely fixed.
 */
export function listHealthVerdictCopy(
  channel: Channel,
  verdict: ListHealthVerdict,
): ListHealthVerdictCopy {
  return LIST_HEALTH_VERDICT_COPY_BY_CHANNEL[channel][verdict];
}

/** Labels for the four wave states. Never a raw enum value on a screen. */
export const CAMPAIGN_WAVE_STATUS_LABELS: Readonly<Record<CampaignWaveStatus, string>> = {
  pending: 'Waiting to start',
  releasing: 'Sending',
  sent: 'Sent, waiting for results',
  cleared: 'Done',
};

/**
 * What the screen says when the campaign is below
 * {@link WARM_UP_MINIMUM_LIST_SIZE}.
 *
 * It says what *does* apply as well as what does not, because "no batches" on
 * its own reads as "no protection", and the halt applies at every size.
 */
export const WARM_UP_NOT_NEEDED_SENTENCE =
  'This list is small enough to send in one go. Sending still stops by itself if too many messages come back as failures.';

/**
 * What the confirmation says when the deployment has the warm-up switched off.
 *
 * It names the volume rather than softening it: the number is the whole point,
 * and somebody pressing Schedule on a cold list is entitled to see it in the
 * sentence rather than infer it from the absence of a batch plan.
 */
export const WARM_UP_DISABLED_SENTENCE =
  'Batching is switched off in this environment, so this campaign goes out to everyone in a single pass with no pause to see how the first messages land.';

/** The half of the sentence above that is true at every size, reused below. */
const WARM_UP_HALT_STILL_APPLIES =
  'Sending still stops by itself if too many messages come back as failures.';

/**
 * The same statement when the number under the floor is **not the size of the
 * list**.
 *
 * A plan is sized on what a campaign will actually *attempt* — the count
 * materialisation calls `sendable`, which is the reachable list less
 * suppressions and less everything the send guard refuses. In a guarded
 * workspace that is 1 out of 526, and the flat sentence above then says *"this
 * list is small enough to send in one go"* about a list of 526 people. It is not
 * small; it is filtered, and telling somebody the wrong one of those is telling
 * them something false about their own data.
 *
 * So the number is named, and which of the three sentences applies is decided
 * from the two counts rather than from a flag: nothing was filtered, some of it
 * was, or all of it was. The *reason* for the filtering is not composed here —
 * `SendGuardNotice` says that with this campaign's own numbers in it, and two
 * accounts of one fact in two tones is what the heading rule forbids.
 */
export function warmUpNotNeededSentence(input: {
  readonly willAttempt: number;
  readonly reachable: number;
  /** Default true so every existing caller keeps its meaning. */
  readonly warmUpEnforced?: boolean;
}): string {
  const willAttempt = Math.max(0, Math.trunc(input.willAttempt));
  const reachable = Math.max(0, Math.trunc(input.reachable));

  // Checked before the size branches, because at this point size is not why
  // there are no batches and saying it is would be false at any number.
  if (input.warmUpEnforced === false) {
    return WARM_UP_DISABLED_SENTENCE;
  }
  if (willAttempt <= 0) {
    return `Nothing on this list will be attempted, so there are no batches to plan. ${WARM_UP_HALT_STILL_APPLIES}`;
  }
  if (willAttempt >= reachable) return WARM_UP_NOT_NEEDED_SENTENCE;

  return (
    `Only ${count(willAttempt)} of the ${count(reachable)} people this campaign reaches will be ` +
    `attempted, which is few enough to send in one go. ${WARM_UP_HALT_STILL_APPLIES}`
  );
}

/**
 * What a person ticks before a halted campaign may be resumed.
 *
 * The claim is the same on both channels — *I have looked, send the rest* — so
 * it is one string rather than a `Record`. Nothing in it is a fact about a
 * provider.
 */
export const RESUME_ACKNOWLEDGEMENT_LABEL =
  'I have looked at this list and want the rest of it sent.';

/**
 * What is at stake when they tick it, **per channel**.
 *
 * This was a literal on `CampaignHaltPanel` and it read *"The next batch goes to
 * real addresses through your own sending account"* — on a WhatsApp campaign,
 * where there are no addresses and no sending account. The panel is drawn
 * whenever `CampaignListSafety.halt` is set, which is on either channel, so a
 * channel-free sentence there is the same class of defect as a channel-free
 * threshold: it is right half the time and nobody can tell which half.
 *
 * It lives here rather than in `list-safety-display.ts` by that file's own rule
 * — the contract writes the sentences and the display layer writes the headings
 * — and because a `Record<Channel, …>` in `@wend/shared` is a compile error when
 * a third channel arrives, where a ternary in a component is not.
 *
 * It says *real*, deliberately. The one thing a person must not be able to think
 * at this moment is that resuming is a rehearsal.
 */
export const RESUME_ACKNOWLEDGEMENT_STAKE_BY_CHANNEL: Readonly<Record<Channel, string>> = {
  email: 'The next batch goes to real addresses through your own Amazon sending account.',
  whatsapp: 'The next batch goes to real phone numbers through your own WhatsApp number.',
};

/** The only supported way to obtain it. There is no channel-free version. */
export function resumeAcknowledgementStake(channel: Channel): string {
  return RESUME_ACKNOWLEDGEMENT_STAKE_BY_CHANNEL[channel];
}

/* -------------------------------------------------------------------------- */
/* 7. The wire shapes                                                          */
/* -------------------------------------------------------------------------- */

/**
 * What the schedule wizard asks for before it commits, and what the campaign
 * screen re-reads afterwards.
 *
 * `plan` is `null` for a list under the floor — **not an empty wave list**,
 * which is the shape that would render as "a plan with no batches" instead of
 * "no plan needed". `warmUp` being absent and warm-up being empty are different
 * statements, which is this codebase's oldest rule applied to an object rather
 * than to a number.
 *
 * ## `willAttempt` IS HERE BECAUSE THE PREVIEW AND THE COMMITTED PLAN HAVE TO BE
 * ## ONE NUMBER
 *
 * The schedule wizard renders `plan` and then, on the confirmation step, tells
 * somebody *"102 go out first and then Wend waits an hour"*. The plan that is
 * actually written at schedule is sized on `MaterialisationSummary.sendable` —
 * the reachable list less suppressions and less everything the send guard
 * refuses. The superseded version of this response sized its `plan` on a
 * different count, one that deliberately did **not** subtract the guard, so in a
 * guarded workspace the confirmation promised six batches and the next page load
 * showed none. A user was agreeing to a warm-up they were never going to get.
 *
 * The number is therefore on the wire rather than implied: `willAttempt` is what
 * `plan` above was computed from, and it is computed by the same rule the
 * schedule path will apply. A reader comparing it with
 * `health.signals.total` can see the gap the guard makes, and a plan of `null`
 * beside a `willAttempt` far below that total is *filtered*, not *small* — which
 * is the distinction {@link warmUpNotNeededSentence} exists to say out loud.
 *
 * It is an **estimate before the list is materialised and a count afterwards**,
 * and that is not hidden either: `WarmUpPlan.plannedForRecipients` records which
 * number a committed plan was built for, so a plan that no longer matches is
 * visibly stale rather than quietly re-sized.
 */
export const campaignListSafetySchema = z
  .object({
    health: listHealthReportSchema,
    plan: warmUpPlanSchema.nullable(),
    /** What `plan` was sized from, by the rule the schedule path uses. See above. */
    willAttempt: z.number().int().min(0),
    /**
     * Slice 61: of the people this send would try, how many the asked-for
     * share would hold back — already subtracted from `willAttempt`. Zero
     * unless the preview was asked with a holdout.
     */
    heldBack: z.number().int().min(0),
    /**
     * Whether this deployment writes warm-up plans at all (`WARM_UP_ENFORCED`).
     *
     * On the wire because `plan: null` has three causes now and the screen must
     * tell them apart: a list under the floor, a list the send guard filtered
     * to under the floor, and the pacing switched off for the whole deployment.
     * Without this the confirmation tells somebody their 1,120-person list is
     * "small enough to send in one go".
     */
    warmUpEnforced: z.boolean(),
    /** Null until the campaign has been scheduled and its waves written. */
    waves: z.array(campaignWaveSchema).nullable(),
    /** Set only while the campaign is halted. */
    halt: z
      .object({
        reason: campaignHaltReasonSchema,
        message: z.string(),
        haltedAt: z.string(),
      })
      .strict()
      .nullable(),
    /** The rate the gate is reading, so the screen shows counts rather than a verdict. */
    reading: rateReadingSchema.nullable(),
  })
  .strict();
export type CampaignListSafety = z.infer<typeof campaignListSafetySchema>;

export const campaignListSafetyResponseSchema = z
  .object({ listSafety: campaignListSafetySchema })
  .strict();
export type CampaignListSafetyResponse = z.infer<typeof campaignListSafetyResponseSchema>;

/**
 * Resuming a halted campaign is an explicit act with its own request body,
 * rather than a re-use of the schedule endpoint.
 *
 * `acknowledged` is `z.literal(true)`: the contract **cannot express** a resume
 * that did not acknowledge the halt, on the same principle
 * `sendGuardStatusSchema.enforced` uses. A person resuming a campaign that
 * stopped because a list is bouncing is taking that decision, and a request
 * shape that can omit it is a shape a client will omit it in.
 */
export const resumeHaltedCampaignRequestSchema = z
  .object({ acknowledged: z.literal(true) })
  .strict();
export type ResumeHaltedCampaignRequest = z.infer<typeof resumeHaltedCampaignRequestSchema>;
